import React from 'react'

/**
 * 📊 Блок формулы расчёта ЗП — структурированные карточки с тарифами,
 * обновляемые в реальном времени при изменении любого параметра.
 */
export default function SalaryFormulaSummary({ settings }) {
  const s = settings || {}

  const fixLabel = s.hourlyRate > 0
    ? `${s.hourlyRate} ₽ × часы`
    : `${s.dailyRate || 0} ₽ × (часы / 12)`

  const helperAccepted = s.helperBonusPerAccepted || s.bonusPerAccepted || 0
  const helperIssued = s.helperBonusPerIssued || s.bonusPerIssued || 0
  const helperReturned = s.helperBonusPerReturned || s.bonusPerReturned || 0

  return (
    <div style={{
      padding: '20px',
      background: 'linear-gradient(135deg, color-mix(in srgb, var(--wb-primary, #8b5cf6) 4%, transparent), color-mix(in srgb, var(--wb-primary, #8b5cf6) 1%, transparent))',
      borderRadius: '16px',
      border: '1px solid color-mix(in srgb, var(--wb-primary, #8b5cf6) 15%, var(--border-color))',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    }}>
      {/* Заголовок блока */}
      <div style={{
        fontSize: '12px',
        fontWeight: 700,
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: '0.6px',
        display: 'flex',
        alignItems: 'center',
        gap: '7px',
        marginBottom: '4px',
      }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
        Формула расчёта начислений
      </div>

      {/* ═══ Карточка 1: Основной сотрудник (без помощника) ═══ */}
      <div style={{
        padding: '14px 16px',
        background: 'var(--surface-color)',
        borderRadius: '12px',
        border: '1px solid var(--border-color)',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '10px',
        }}>
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
            Основной сотрудник (без помощника)
          </span>
          <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>Стандартный режим</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12.5px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px dashed var(--border-color, rgba(255,255,255,0.06))' }}>
            <span style={{ color: 'var(--text-secondary)' }}>Фиксированная часть</span>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{fixLabel}</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px dashed var(--border-color, rgba(255,255,255,0.06))' }}>
            <span style={{ color: 'var(--text-secondary)' }}>Сдельная приёмка</span>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{s.bonusPerAccepted || 0} ₽ / шт</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px dashed var(--border-color, rgba(255,255,255,0.06))' }}>
            <span style={{ color: 'var(--text-secondary)' }}>Сдельная выдача</span>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{s.bonusPerIssued || 0} ₽ / шт</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px dashed var(--border-color, rgba(255,255,255,0.06))' }}>
            <span style={{ color: 'var(--text-secondary)' }}>Сдельные возвраты</span>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{s.bonusPerReturned || 0} ₽ / шт</span>
          </div>

          {s.turnoverPercent > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px dashed var(--border-color, rgba(255,255,255,0.06))' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Процент от оборота</span>
                <span style={{
                  fontSize: '10px',
                  padding: '1px 6px',
                  borderRadius: '4px',
                  background: 'rgba(156, 163, 175, 0.15)',
                  color: 'var(--text-tertiary, #9ca3af)',
                  fontWeight: 500,
                  border: '1px solid rgba(156, 163, 175, 0.25)',
                }}>
                  В разработке
                </span>
              </div>
              <span style={{ fontWeight: 500, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
                {s.turnoverPercent}% (не начисл.)
              </span>
            </div>
          )}

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '8px',
            marginTop: '4px',
            borderTop: '1px solid var(--border-color)',
            fontWeight: 700,
          }}>
            <span style={{ color: 'var(--text-primary)' }}>Итого к начислению</span>
            <span style={{ color: 'var(--wb-primary, #8b5cf6)' }}>Фикс + Сделка − Штрафы</span>
          </div>
        </div>
      </div>

      {/* ═══ Сотрудник с помощником + Помощник ═══ */}
      {s.enableHelpers !== false && (
        <>
          {/* Карточка 2: Основной сотрудник с помощником */}
          <div style={{
            padding: '14px 16px',
            background: 'var(--surface-color)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '10px',
            }}>
              <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                Основной сотрудник (с помощником)
              </span>
              <span style={{ fontSize: '11px', color: '#10B981', fontWeight: 500 }}>Помощник на смене</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12.5px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px dashed var(--border-color, rgba(255,255,255,0.06))' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Фиксированная часть</span>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{fixLabel}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px dashed var(--border-color, rgba(255,255,255,0.06))' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Приёмка (с помощником)</span>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{helperAccepted} ₽ / шт</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px dashed var(--border-color, rgba(255,255,255,0.06))' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Выдача (с помощником)</span>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{helperIssued} ₽ / шт</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px dashed var(--border-color, rgba(255,255,255,0.06))' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Возвраты (с помощником)</span>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{helperReturned} ₽ / шт</span>
              </div>

              {s.helperDeductionPercent > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px dashed var(--border-color, rgba(255,255,255,0.06))' }}>
                  <span style={{ color: 'var(--danger, #ef4444)' }}>Удержание за помощника</span>
                  <span style={{ fontWeight: 600, color: 'var(--danger, #ef4444)', fontVariantNumeric: 'tabular-nums' }}>−{s.helperDeductionPercent}% от ЗП пом.</span>
                </div>
              )}

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: '8px',
                marginTop: '4px',
                borderTop: '1px solid var(--border-color)',
                fontWeight: 700,
              }}>
                <span style={{ color: 'var(--text-primary)' }}>Итого к начислению</span>
                <span style={{ color: 'var(--wb-primary, #8b5cf6)' }}>
                  Фикс + Сделка {s.helperDeductionPercent > 0 ? '− Удержание ' : ''}− Штрафы
                </span>
              </div>
            </div>
          </div>

          {/* Карточка 3: Помощник */}
          <div style={{
            padding: '14px 16px',
            background: 'var(--surface-color)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '10px',
            }}>
              <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                Помощник
              </span>
              <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>Почасовая ставка</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12.5px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px dashed var(--border-color, rgba(255,255,255,0.06))' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Почасовая ставка помощника</span>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{s.helperHourlyRate || 0} ₽ / час</span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: '8px',
                marginTop: '4px',
                borderTop: '1px solid var(--border-color)',
                fontWeight: 700,
              }}>
                <span style={{ color: 'var(--text-primary)' }}>Итого к начислению</span>
                <span style={{ color: '#10B981' }}>Часы × {s.helperHourlyRate || 0} ₽ − Штрафы</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══ Карточка 4: Дополнительные правила ═══ */}
      {s.customRules && s.customRules.length > 0 && (
        <div style={{
          padding: '14px 16px',
          background: 'var(--surface-color)',
          borderRadius: '12px',
          border: '1px solid var(--border-color)',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '10px',
          }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
              Индивидуальные правила (Custom Rules)
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{s.customRules.length} правил(а)</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12.5px' }}>
            {s.customRules.map(rule => (
              <div key={rule.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px dashed var(--border-color, rgba(255,255,255,0.06))' }}>
                <span style={{ color: 'var(--text-secondary)' }}>{rule.name}</span>
                <span style={{ fontWeight: 600, color: 'var(--wb-primary, #8b5cf6)', fontVariantNumeric: 'tabular-nums' }}>
                  {rule.type === 'fixed'
                    ? `+${rule.amount} ₽ при выходе`
                    : rule.type === 'percent'
                    ? `+${rule.amount}% от операций`
                    : `+${rule.amount} ₽ / операция`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
